import { ChangeDetectionStrategy, Component, OnInit, output, signal, WritableSignal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, finalize, of, takeUntil, tap } from 'rxjs';
import {
    AutoFocusDirective,
    BaseComponent,
    ErrorsUtil,
    InputComponent,
    MessageBoxComponent,
    MessageContent,
    WaitButtonComponent
} from '@sircon/sircon-ui-framework';

import { CognitoMfaMethod } from 'src/app/core/models/login/mfa-enrollment.model';
import { LoginHttpService } from 'src/providers/login-http.service';
import { ProtectedActionsService } from 'src/services/protected-actions.service';

export const REAUTH_DIALOG_ID = 'reauth-dialog';

type ReAuthStep = 'password' | 'mfa-challenge';

@Component({
    selector: 'app-re-auth',
    templateUrl: './re-auth.component.html',
    styleUrls: ['./re-auth.component.less'],
    imports: [ReactiveFormsModule, MessageBoxComponent, InputComponent, WaitButtonComponent, AutoFocusDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReAuthComponent extends BaseComponent implements OnInit {
    readonly verified = output<void>();
    readonly cancelled = output<void>();

    currentStep: WritableSignal<ReAuthStep> = signal('password');
    isVerifying: WritableSignal<boolean> = signal(false);
    errors: MessageContent;

    passwordForm: FormGroup;
    mfaChallengeForm: FormGroup;

    mfaChallengeSession: string;
    mfaChallengeMethod: CognitoMfaMethod;

    readonly mfaChallengeInfoByMethod: Partial<Record<CognitoMfaMethod, string>> = {
        [CognitoMfaMethod.SOFTWARE_TOKEN_MFA]: 'Enter the verification code from your authenticator app to continue.',
        [CognitoMfaMethod.SMS_MFA]: 'Enter the verification code sent to your phone to continue.'
    };

    constructor(
        private formBuilder: FormBuilder,
        private loginHttpService: LoginHttpService,
        private protectedActionsService: ProtectedActionsService
    ) {
        super();
    }

    ngOnInit(): void {
        this.errors = ErrorsUtil.create();

        this.passwordForm = this.formBuilder.group({
            currentPassword: ['', Validators.required]
        });

        this.mfaChallengeForm = this.formBuilder.group({
            verificationCode: ['', Validators.compose([
                Validators.required,
                Validators.minLength(6),
                Validators.maxLength(6),
                Validators.pattern(/^[0-9]+$/)
            ])]
        });
    }

    onContinue(): void {
        if (!this.passwordForm.valid) {
            this.errors.validationError = true;
            this.errors.globalErrors = ErrorsUtil.buildMessage('Please enter your current password.');
            return;
        }

        this.isVerifying.set(true);
        ErrorsUtil.clear(this.errors);

        this.loginHttpService.reAuthenticateUser({ currentPassword: this.passwordForm.value.currentPassword })
            .pipe(
                tap(response => {
                    if (response?.challengeName === CognitoMfaMethod.SOFTWARE_TOKEN_MFA ||
                        response?.challengeName === CognitoMfaMethod.SMS_MFA) {
                        this.mfaChallengeSession = response.session;
                        this.mfaChallengeMethod = response.challengeName;
                        this.currentStep.set('mfa-challenge');
                    } else if (!response?.challengeName) {
                        this.onVerified();
                    } else {
                        this.errors.validationError = true;
                        this.errors.globalErrors = ErrorsUtil.buildMessage('This account uses an unsupported verification method.');
                    }
                }),
                catchError(error => {
                    this.errors = error.status === 403
                        ? { ...ErrorsUtil.create(), validationError: true }
                        : ErrorsUtil.mapResponse(error);
                    return of(null);
                }),
                finalize(() => this.isVerifying.set(false)),
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe();
    }

    onSubmitMfaChallenge(): void {
        if (!this.mfaChallengeForm.valid) {
            this.errors.validationError = true;
            this.errors.globalErrors = ErrorsUtil.buildMessage('Please enter your 6-digit verification code.');
            return;
        }

        this.isVerifying.set(true);
        ErrorsUtil.clear(this.errors);

        this.loginHttpService.reVerifyChallenge({
            challengeName: this.mfaChallengeMethod,
            code: this.mfaChallengeForm.value.verificationCode,
            session: this.mfaChallengeSession
        })
            .pipe(
                tap(() => this.onVerified()),
                catchError(error => {
                    this.errors = error.status === 403
                        ? { ...ErrorsUtil.create(), validationError: true }
                        : ErrorsUtil.mapResponse(error);
                    return of(null);
                }),
                finalize(() => this.isVerifying.set(false)),
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe();
    }

    onCancel(): void {
        this.protectedActionsService.cancelReAuth();
        this.reset();
        this.cancelled.emit();
    }

    private onVerified(): void {
        this.protectedActionsService.completeReAuth();
        this.reset();
        this.verified.emit();
    }

    private reset(): void {
        this.currentStep.set('password');
        this.passwordForm.reset();
        this.mfaChallengeForm.reset();
        ErrorsUtil.clear(this.errors);
    }
}
